/**
 * AttributionDialog.tsx — 交易导入人工归属弹窗（ADM-FIN-S009）
 *
 * 「确认归属」走 POST /api/v1/finance/transaction-imports/attribution
 * （financeApi.attributeTransactionImport + useMutation），body 严格按 shared
 * TransactionImportAttributionRequest：{ app_id, transaction_id, resolved_user_id(UUID),
 * resolution_note(1..2000 非空白) }。
 *
 * resolved_user_id 由运营线下核验后录入（工单 / 收据比对）：输入合法 UUID → 即时 usersApi.detail
 * 预览用户（名称 / 邮箱 / App / 状态），便于提交前肉眼核对归属对象；跨 App 用户（user.app_id ≠ 交易
 * app_id）前端**只预警、不阻断** —— 后端自 #503 起把该校验由「等值」放开成「同一 Adapty app 的
 * 兄弟行集合」（`02-API契约/A1-运营财务.md` §10.2 + `03-数据字典/60-订阅.md` §3.0），iOS / Android
 * 两行互为兄弟时跨 App 归属是**合法**的。前端判不出兄弟关系（`user.app_id` 只是单个 app_id、
 * 兄弟行集合要查 `apps.adapty_app_id`），拿它硬拒会把合法归属一起挡掉，故只做提示、由后端裁定。
 * resolution_note = 归属依据摘要（审计必填）。
 * 成功 toast + invalidate ['transaction-imports'] + 关闭；失败按 code 中文 toast
 * （FINANCE_IMPORT_ATTRIBUTION_CONFLICT = 已投影不可改绑 / 已归属，见 error-messages.ts）。
 *
 * 写端点无请求级幂等键 → financeApi 已设 replayOnUnauthorized:false（避免 401 续期后重复改绑）。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useUserDetail } from '~/data/queries';
import type { TxnImportQueueRow } from '~/data/queries';
import { financeApi } from '~/hooks/use-finance';
import { currencySymbol } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { toast } from '~/ui/Toast';

// 与 sibling 弹窗一致的 FormRow 几何（labelWidth 140）
const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

// RFC 4122 UUID 格式校验：仅格式合法才触发用户预览 + 放行提交（后端仍权威校验存在性）
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function AttributionDialog({
  row,
  onClose,
}: {
  row: TxnImportQueueRow;
  onClose: () => void;
}) {
  const [resolvedUserId, setResolvedUserId] = useState('');
  const [note, setNote] = useState('');
  const uid = resolvedUserId.trim();
  const uuidValid = UUID_RE.test(uid);
  // UUID 合法才拉用户预览（enabled 门控，非法 / 半截 UUID 不打后端）
  const { data: user, isLoading: userLoading, error: userError } = useUserDetail(uid, uuidValid);
  // 区分「用户不存在」(RESOURCE_NOT_FOUND) 与「加载失败」(传输/5xx)：前者提示不存在，后者提示重试，
  // 不把瞬时故障误标成「未找到」而静默阻断合法归属。
  const userNotFound =
    userError instanceof AdminApiError && userError.code === 'RESOURCE_NOT_FOUND';
  const userLoadError = !!userError && !userNotFound;
  // 跨 App **预警**（best-effort，不参与 canSubmit）：user.app_id 可为 null（无 session 用户），
  // 且它只是单个 app_id —— 判不出「是不是同一 Adapty app 的兄弟行」（那要查 apps.adapty_app_id）。
  // 故这里只提示运营多看一眼，放行与否一律由后端裁定（集合外仍返 VALIDATION_FAILED）。
  const crossApp = !!user?.app_id && user.app_id !== row.app_id;

  const queryClient = useQueryClient();
  const attributeMutation = useMutation({
    mutationFn: () =>
      financeApi.attributeTransactionImport({
        app_id: row.app_id,
        transaction_id: row.transaction_id,
        resolved_user_id: uid,
        resolution_note: note.trim(),
      }),
    onSuccess: () => {
      toast(`交易 ${row.transaction_id} 已归属 · 待下轮 reconcile 重放投影`, {
        tone: 'pos',
        icon: 'IconLink',
      });
      queryClient.invalidateQueries({ queryKey: ['transaction-imports'] });
      onClose();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '归属失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  const noteTrimLen = note.trim().length;
  const canSubmit =
    uuidValid &&
    !!user && // 预览取到 = 用户存在
    noteTrimLen > 0 &&
    noteTrimLen <= 2000 &&
    !attributeMutation.isPending;

  const sym = row.base_currency ? currencySymbol(row.base_currency) : '';

  return (
    <Dialog
      width={520}
      title="交易人工归属"
      subtitle={`${row.transaction_id} · ${row.appName}`}
      icon="IconLink"
      onClose={onClose}
      footer={
        <>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            归属后由下一轮 reconcile 自动投影 · 操作记入审计日志
          </span>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!canSubmit}
            onClick={() => attributeMutation.mutate()}
          >
            <Icons.IconLink size={11} />
            确认归属
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        {/* 交易上下文（只读）：本币额 / 产品，供对照用户收据核验 */}
        <FormRow label="交易" hint="原币本币额 · 对照用户收据核验">
          <div className="vstack" style={{ gap: 4 }}>
            <div className="mono" style={{ fontSize: 12.5 }}>
              {row.base_amount_local ? `${sym}${row.base_amount_local}` : '—'}{' '}
              <span className="muted">{row.base_currency ?? ''}</span>
            </div>
            <div className="mono muted" style={{ fontSize: 11 }}>
              {row.vendor_product_id}
            </div>
          </div>
        </FormRow>
        <FormRow label="归属用户" required hint="填入用户 UUID（线下工单 / 收据核验后确定）">
          <div className="vstack" style={{ gap: 6 }}>
            <input
              className="input mono"
              style={{ fontSize: 12 }}
              placeholder="0190a203-8e4f-71a5-9c2d-1a2b3c4d5e6f"
              value={resolvedUserId}
              onChange={(e) => setResolvedUserId(e.target.value)}
            />
            {/* 用户预览：合法 UUID 即拉 usersApi.detail 核对，避免归属错人 */}
            {uuidValid && (
              <div className="card-flat" style={{ padding: '8px 10px', fontSize: 11.5 }}>
                {userLoading ? (
                  <span className="muted">核对用户中…</span>
                ) : user ? (
                  <div className="vstack" style={{ gap: 2 }}>
                    <div>
                      <span style={{ fontWeight: 500 }}>{user.display_name ?? '（无昵称）'}</span>{' '}
                      <span className="mono muted">{user.display_user_id}</span>
                    </div>
                    <div className="muted">
                      {user.email ?? '无邮箱'} · {user.status === 'BANNED' ? '已封禁' : '正常'}
                    </div>
                    {crossApp && (
                      <div style={{ color: 'var(--warn)' }}>
                        ⚠ 用户归属 App 与本交易 App 不一致 —— 仅当两者是同一 Adapty app
                        的兄弟行（iOS / Android）时才可归属，请先核对再提交
                      </div>
                    )}
                  </div>
                ) : userLoadError ? (
                  // 加载失败（传输/5xx）：不暗示用户不存在，提示重试
                  <span style={{ color: 'var(--neg)' }}>用户信息加载失败，请重试</span>
                ) : userNotFound ? (
                  <span style={{ color: 'var(--neg)' }}>未找到该用户</span>
                ) : null}
              </div>
            )}
          </div>
        </FormRow>
        <FormRow
          label="归属依据"
          required
          hint="审计必填 · 记录归属判定依据（工单号 / 收据比对结论），≤ 2000 字"
        >
          <textarea
            className="input"
            rows={3}
            style={{ fontSize: 12, resize: 'vertical', width: '100%' }}
            placeholder="如：用户工单 #12345 提供 App Store 收据，交易号与购买时间一致"
            value={note}
            maxLength={2000}
            onChange={(e) => setNote(e.target.value)}
          />
        </FormRow>
      </div>
    </Dialog>
  );
}
